import * as React from 'react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TranslatedFileInputProps {
  accept?: string;
  onChange: (file: File | null) => void;
  value?: File | null;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function TranslatedFileInput({ 
  accept, 
  onChange, 
  value, 
  disabled, 
  className,
  id 
}: TranslatedFileInputProps) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onChange(file);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn("flex items-center gap-2 mt-2", className)}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleChange}
        disabled={disabled}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={disabled}
        className="flex items-center gap-2 flex-shrink-0"
      >
        <Upload className="h-4 w-4" />
        {t('common.chooseFile')}
      </Button>
      <span className="text-sm text-muted-foreground flex-1 truncate min-w-0">
        {value ? value.name : t('common.noFileChosen')}
      </span>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0"
          onClick={handleClear}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
